import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, Observable, of, forkJoin } from 'rxjs';
import { skip, tap, catchError, finalize } from 'rxjs/operators';
import { LoginService } from 'src/app/services/login.service';
import { CustomerService } from 'src/app/services/customer.service';
import { WarrantyService } from 'src/app/services/warranty.service';
import { DealerPortalService } from 'src/app/services/dealerPortal.service';
import { RebateContractService } from 'src/app/services/rebate-contract.service';
import { WorkshopService } from 'src/app/services/workshop.service';
import { SharedDataService } from 'src/app/services/sharedDataService.service';
import { DocumentManagementService } from 'src/app/services/document-management.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';

interface RecommendedProduct {
  name: string;
  id: string;
  description: string;
  tag: string;
  tagType: 'new' | 'frequent' | 'promo';
  image: string;
}

interface BannerItem {
  image: string;
  title: string;
  navigationUrl: string;
}

@Component({
  selector: 'home-page-backup',
  templateUrl: './home-page-backup.component.html',
  styleUrls: ['./home-page-backup.component.less'],
})
export class HomePageBackupComponent implements OnInit, OnDestroy {

  /** 默认 Banner：接口获取数据时先展示这张图片，拉取到的数据拼接在其后 */
  private readonly defaultBanner: BannerItem = {
    image: 'assets/homepage/banner-battery.jpg',
    title: '',
    navigationUrl: '',
  };

  bannerList: BannerItem[] = [this.defaultBanner];
  /** Banner 独立加载标记：接口已剥离单独渲染，拿到数据前展示占位 loading，拿到后立即展示 */
  bannerLoading = true;
  currentBannerIndex = 0;

  /** News 独立加载标记：接口已剥离单独渲染，未拿到数据时展示三个点 loading */
  newsLoading = true;

  /** 看板全页加载标记：仅控制 Section 2~5，所有接口返回前展示 loading，返回后统一渲染 */
  pageLoading = true;

  // 各接口权限标记：接口返回 rtnCode=10010（无权限）时对应模块不展示，
  // 仅当接口调用成功（rtnCode=0/10000）时才置为 true。售出管理模块以
  // stationGroups.length 是否 > 0 作为权限+数据的双重判定（其 service 已剥离 rtnCode）。
  permOrder = false;
  permAnnual = false;
  permProductLine = false;
  permRebate = false;
  permNews = false;

  /** 采购目标区域（Section2 / Section4）是否存在任一有权限的模块 */
  get hasPurchaseModule(): boolean {
    return this.permOrder || this.permAnnual || this.permProductLine;
  }

  /** 返利三项金额（目标/已使用/已发放）是否存在非 0 值，全为 0 时返利板块不展示 */
  get hasRebateData(): boolean {
    return !!(this.rebateData.targetRebateAmount || this.rebateData.settledRebateAmount || this.rebateData.paidRebateAmount);
  }

  /** 订单追踪净金额是否非 0，为 0 时订单追踪板块不展示 */
  get hasOrderData(): boolean {
    return !!this.orderTotalNetAmount;
  }
  private bannerAutoTimer: any = null;
  private customerSubscription: Subscription | undefined;

  get currentBanner(): BannerItem | null {
    return this.bannerList[this.currentBannerIndex] || null;
  }

  getBannerTrackTransform(): string {
    return `translateX(-${this.currentBannerIndex * 100}%)`;
  }

  selectedCustomer = '江苏康众汽配有限公司 _0118000033';

  rebateData: {
    targetRebateAmount: number;
    settledRebateAmount: number;
    paidRebateAmount: number;
    latestUpdateTime: string;
  } = {
    targetRebateAmount: 0,
    settledRebateAmount: 0,
    paidRebateAmount: 0,
    latestUpdateTime: '',
  };

  constructor(
    private loginService: LoginService,
    private customerService: CustomerService,
    private warrantyService: WarrantyService,
    private dealerPortalService: DealerPortalService,
    private rebateContractService: RebateContractService,
    private workshopService: WorkshopService,
    private sharedData: SharedDataService,
    private documentService: DocumentManagementService,
    private message: NzMessageService,
    private router: Router,
  ) {}

  orderStats: {
    label: string; value: number; bgColor: string; icon: string;
  }[] = [
    { label: 'HOMEPAGE_ORDER_STATUS_OPEN', value: 0, bgColor: '#EFF6FF', icon: 'assets/homepage/open-order.png' },
    { label: 'HOMEPAGE_ORDER_STATUS_PENDING_PAYMENT', value: 0, bgColor: '#FFF7ED', icon: 'assets/homepage/payment.png' },
    { label: 'HOMEPAGE_ORDER_STATUS_IN_WAREHOUSE', value: 0, bgColor: '#FAF5FF', icon: 'assets/homepage/warehouse.png' },
    { label: 'HOMEPAGE_ORDER_STATUS_GOODS_ISSUED', value: 0, bgColor: '#F0FDF4', icon: 'assets/homepage/delivery.png' },
  ];

  orderTotalNetAmount = 0;
  orderLatestUpdateTime = '';

  productLines: { name: string; value: number; color: string }[] = [];

  get productLineHalf(): number {
    return Math.ceil(this.productLines.length / 2);
  }

  private readonly productLineColorPalette = [
    '#007BC0', '#18837E', '#9E2896', '#F19F23',
    '#00884A', '#EEC100', '#E03E3E', '#0891b2',
  ];

  productLineAmounts: { name: string; amount: string }[] = [];

  stationGroups: {
    label: string; target: string; current: string; total: string;
    percent: number; labelBg: string; labelColor: string; strokeColor: string;
    wide?: boolean; extra?: boolean;
  }[] = [];

  newsItems: { date: string; title: string; desc: string; introductionFile?: string; fileName?: string }[] = [];

  annualOverviewLatestUpdateTime = '';

  annualOverview: {
    currentTargetAchievement: number;
    annualPurchaseTarget: string;
    actualPurchaseAmount: string;
    quarterlyTarget: string;
    pendingPurchaseAmountYTM: string;
    quarterlyPendingPurchaseAmount: string;
  } = {
    currentTargetAchievement: 0,
    annualPurchaseTarget: '0',
    actualPurchaseAmount: '0',
    quarterlyTarget: '0',
    pendingPurchaseAmountYTM: '0',
    quarterlyPendingPurchaseAmount: '0',
  };

  // 今日发货金额（来源 getOrderSummary），用于估算额外达成百分比
  todayShippedAmount = 0;

  // 额外预估百分比 = 今日发货金额 / 年度采购目标
  get estimatedExtraPercent(): number {
    const target = this.purchaseTargetDisplay;
    if (!target) return 0;
    return Math.round((this.todayShippedAmount / target) * 100);
  }

  readonly ringSize = 200;
  readonly ringRadius = 72;
  readonly progressBarLeft = 31;
  readonly progressBarWidth = 229;
  readonly progressBarTop = 247;

  showProductRecommend = true;
  productPanelPosition: { x: number | null; y: number | null } = { x: null, y: null };
  isDraggingProductPanel = false;
  private productPanelDragOffset = { x: 0, y: 0 };

  recommendedProducts: RecommendedProduct[] = [
    {
      name: 'Aerotwin雨刮片',
      id: '3397014981',
      description: '这里是产品的介绍...',
      tag: '新品',
      tagType: 'new',
      image: 'assets/official-website/banner13.png',
    },
    {
      name: 'Aerotwin雨刮片',
      id: '3397014981',
      description: '这里是产品的介绍...',
      tag: '常买',
      tagType: 'frequent',
      image: 'assets/official-website/banner13.png',
    },
    {
      name: 'Aerotwin雨刮片',
      id: '3397014981',
      description: '这里是产品的介绍...',
      tag: '促销',
      tagType: 'promo',
      image: 'assets/official-website/banner13.png',
    },
    {
      name: 'Aerotwin雨刮片',
      id: '3397014981',
      description: '这里是产品的介绍...',
      tag: '新品',
      tagType: 'new',
      image: 'assets/official-website/banner13.png',
    },
  ];

  get achievementPercent(): number {
    return Math.min(100, Math.round(this.annualOverview.currentTargetAchievement * 100));
  }

  get purchaseTargetDisplay(): number {
    return this.parseFormattedNumber(this.annualOverview.annualPurchaseTarget);
  }

  /** 是否有采购目标：有则展示 purchase-section，无则展示 purchase-section-2 */
  get hasPurchaseTarget(): boolean {
    return this.purchaseTargetDisplay > 0;
  }

  get actualPurchaseAmountDisplay(): number {
    return this.parseFormattedNumber(this.annualOverview.actualPurchaseAmount);
  }

  get quarterlyTargetDisplay(): number {
    return this.parseFormattedNumber(this.annualOverview.quarterlyTarget);
  }

  get quarterlyPendingDisplay(): number {
    return this.parseFormattedNumber(this.annualOverview.quarterlyPendingPurchaseAmount);
  }

  private parseFormattedNumber(value: string): number {
    if (!value || value === '-') return 0;
    return parseFloat(value.replace(/,/g, ''));
  }

  get connectorAnchorPercent(): number {
    return this.achievementPercent + this.estimatedExtraPercent / 2;
  }

  get connectorLineStyle(): { [key: string]: string } {
    const anchorPercent = this.connectorAnchorPercent;
    const theta = (anchorPercent / 100) * 2 * Math.PI;
    const donutSectionTop = 64;
    const ringCenterY = donutSectionTop + this.ringSize / 2;
    const ringPointY = ringCenterY - this.ringRadius * Math.cos(theta);
    const barAnchorX = this.progressBarLeft + (anchorPercent / 100) * this.progressBarWidth;
    const lineTop = ringPointY + this.ringRadius * 0.12;
    const lineHeight = Math.max(this.progressBarTop - lineTop, 0);

    return {
      left: `${barAnchorX}px`,
      top: `${lineTop}px`,
      height: `${lineHeight}px`,
    };
  }

  get productPanelStyle(): { [key: string]: string } {
    if (this.productPanelPosition.x !== null && this.productPanelPosition.y !== null) {
      return {
        top: this.productPanelPosition.y + 'px',
        left: this.productPanelPosition.x + 'px',
        right: 'auto',
      };
    }
    return {};
  }

  onProductPanelHeaderMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('button')) return;

    this.isDraggingProductPanel = true;
    const panelEl = (event.currentTarget as HTMLElement).closest('.product-recommend-panel') as HTMLElement;
    if (!panelEl) return;

    const rect = panelEl.getBoundingClientRect();
    this.productPanelDragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    if (this.productPanelPosition.x === null) {
      const wrapper = panelEl.closest('.homepage-dashboard') as HTMLElement;
      const wrapperRect = wrapper.getBoundingClientRect();
      this.productPanelPosition.x = rect.left - wrapperRect.left;
      this.productPanelPosition.y = rect.top - wrapperRect.top;
    }

    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onProductPanelMouseMove(event: MouseEvent): void {
    if (!this.isDraggingProductPanel) return;

    const panelEl = document.querySelector('.product-recommend-panel') as HTMLElement;
    const wrapper = panelEl?.closest('.homepage-dashboard') as HTMLElement;
    if (!wrapper) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    this.productPanelPosition.x = event.clientX - wrapperRect.left - this.productPanelDragOffset.x;
    this.productPanelPosition.y = event.clientY - wrapperRect.top - this.productPanelDragOffset.y;
  }

  @HostListener('document:mouseup')
  onProductPanelMouseUp(): void {
    this.isDraggingProductPanel = false;
  }

  closeProductRecommend(): void {
    this.showProductRecommend = false;
  }

  /** 跳转至年度目标达成详情报表页（路由带 :country 前缀） */
  goToTargetAchievement(): void {
    const country = localStorage.getItem('country');
    this.router.navigateByUrl(`${country}/target-achievement`);
  }

  /** 跳转至返利合同页（路由带 :country 前缀） */
  goToRebateContracts(): void {
    const country = localStorage.getItem('country');
    this.router.navigateByUrl(`${country}/contracts_rebates`);
  }

  /** 跳转至售出管理（Workshop）页（路由带 :country 前缀） */
  goToWorkShop(): void {
    const country = localStorage.getItem('country');
    this.router.navigateByUrl(`${country}/work-shop`);
  }

  onBannerClick(banner?: BannerItem): void {
    const url = banner?.navigationUrl;
    if (!url) return;
    // 仅允许 http(s) 外链或站内路径，避免 javascript: 等危险协议
    if (!/^(https?:)?\/\//i.test(url) && !url.startsWith('/')) return;
    if (url.startsWith('http')) {
      window.open(url, '_blank');
    } else {
      this.router.navigateByUrl(url);
    }
  }

  goToBanner(index: number): void {
    if (index < 0 || index >= this.bannerList.length || index === this.currentBannerIndex) {
      return;
    }
    this.currentBannerIndex = index;
    this.restartBannerAutoPlay();
  }

  private startBannerAutoPlay(): void {
    this.stopBannerAutoPlay();
    if (this.bannerList.length <= 1) return;
    this.bannerAutoTimer = setInterval(() => {
      this.currentBannerIndex = (this.currentBannerIndex + 1) % this.bannerList.length;
    }, 6000);
  }

  private restartBannerAutoPlay(): void {
    this.startBannerAutoPlay();
  }

  private stopBannerAutoPlay(): void {
    if (this.bannerAutoTimer) {
      clearInterval(this.bannerAutoTimer);
      this.bannerAutoTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.sharedData.silentNoPermission = false;
    this.stopBannerAutoPlay();
    this.customerSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    // 首页各模块接口在无权限(rtnCode 10010)时静默处理，仅隐藏模块、不弹"没有权限"提示
    this.sharedData.silentNoPermission = true;

    // 切换 /iam <-> /oes 后，customer-select 会在其自身 ngOnInit 中修正客户为新国家的客户并
    // 触发下方订阅；只要 selectedCustomer 为空或国家不一致，customer-select 一定会调用
    // setSelectedCustomer/next() 产生修正事件，所以此时都应跳过立即加载，只有客户已存在且
    // 国家一致（不会再有修正事件）时才需要立即加载
    const selectedCustomer = this.customerService.getSelectedCustomer();
    const currentCountry = localStorage.getItem('country');
    if (selectedCustomer && selectedCustomer.country === currentCountry) {
      this.fetchCustomerData();
    }
    // 切换客户时重新获取当前页面数据（skip(1) 跳过 BehaviorSubject 的初始值）
    this.customerSubscription = this.customerService.selectedCustomer$
        .pipe(skip(1))
        .subscribe(() => {
      this.fetchCustomerData();
    });
  }

  /**
   * 拉取与客户相关的看板数据。
   * Banner 与 News 接口剥离，单独订阅、单独渲染（各自管理加载态），
   * 不参与看板统一 loading；Section 2~5 的接口用 forkJoin 统一等待后再渲染。
   */
  private fetchCustomerData(): void {
    // Banner / News：单独渲染，拿到数据即展示
    this.loadBannerData();
    this.fetchNews();

    // 看板 Section 2~5：统一等待所有接口返回后一次性渲染
    this.pageLoading = true;
    forkJoin([
      this.fetchOrderSummary(),
      this.fetchAnnualData(),
      this.fetchProductLines(),
      this.fetchRebateData(),
      this.fetchStationGroups(),
    ]).subscribe({
      next: () => { this.pageLoading = false; },
      error: () => { this.pageLoading = false; },
    });
  }

  /** 订单追踪卡片数据：来源 Dealer Homepage getOrderSummary */
  private fetchOrderSummary(): Observable<any> {
    return this.dealerPortalService.getOrderSummary().pipe(
      tap((result) => {
        const { rtnCode, rtnData } = result;
        this.permOrder = rtnCode === 0 || rtnCode === 10000;
        if ((rtnCode === 0 || rtnCode === 10000) && rtnData) {
          const amounts = [
            rtnData.openOrderAmount,
            rtnData.pendingPaymentAmount,
            rtnData.warehouseProcessingAmount,
            rtnData.todayShippedAmount,
          ];
          this.orderStats = this.orderStats.map((stat, index) => ({
            ...stat,
            value: amounts[index] ?? 0,
          }));
          this.orderTotalNetAmount = rtnData.totalNetAmount ?? 0;
          this.orderLatestUpdateTime = rtnData.lastUpdateTime;
          this.todayShippedAmount = rtnData.todayShippedAmount ?? 0;
        } else {
          // 无权限或返回空数据：清零，避免残留上一个客户的订单追踪数据
          this.orderStats = this.orderStats.map((stat) => ({ ...stat, value: 0 }));
          this.orderTotalNetAmount = 0;
          this.orderLatestUpdateTime = '';
          this.todayShippedAmount = 0;
        }
      }),
      catchError(() => {
        // 接口异常：清零，避免残留上一个客户的订单追踪数据
        this.orderStats = this.orderStats.map((stat) => ({ ...stat, value: 0 }));
        this.orderTotalNetAmount = 0;
        this.orderLatestUpdateTime = '';
        this.todayShippedAmount = 0;
        return of(null);
      }),
    );
  }

  private readonly stationColorMap: Record<string, { labelBg: string; strokeColor: string }> = {
    'FRC':     { labelBg: '#D1E4FF', strokeColor: '#007BC0' },
    'BSS':     { labelBg: '#F0DCEE', strokeColor: '#9E2896' },
    'GBB-电池': { labelBg: '#FFDF95', strokeColor: '#EEC100' },
    'CV Plan': { labelBg: '#FFD697', strokeColor: '#F19F23' },
    'BDX':     { labelBg: '#B6EDE8', strokeColor: '#18837E' },
  };

  private readonly stationColorPalette = [
    { labelBg: '#D1E4FF', strokeColor: '#007BC0' },
    { labelBg: '#F0DCEE', strokeColor: '#9E2896' },
    { labelBg: '#FFDF95', strokeColor: '#EEC100' },
    { labelBg: '#B8EFC9', strokeColor: '#00884A' },
    { labelBg: '#FFD697', strokeColor: '#F19F23' },
    { labelBg: '#B6EDE8', strokeColor: '#18837E' },
  ];

  private fetchStationGroups(): Observable<any> {
    const year = localStorage.getItem('selectedYear') || '';
    return this.workshopService.getTargetGroup({ year }).pipe(
      tap((result) => {
        const list = result.workshopTargetVO || [];
        this.stationGroups = list.map((item, index) => {
          const colors = this.stationColorMap[item.workshopType]
            || this.stationColorPalette[index % this.stationColorPalette.length];
          const percent = item.totalWorkshop > 0
            ? Math.round((item.targetAchWorkshop / item.totalWorkshop) * 100)
            : 0;
          return {
            label: item.workshopType || '',
            target: `¥${item.gtvPerClientType?.toLocaleString() || '0'}`,
            current: String(item.targetAchWorkshop ?? 0),
            total: String(item.totalWorkshop ?? 0),
            percent,
            labelBg: colors.labelBg,
            labelColor: '#191919',
            strokeColor: colors.strokeColor,
          };
        });
      }),
      catchError(() => {
        // 接口异常：清空，避免残留上一个客户的售出管理数据
        this.stationGroups = [];
        return of(null);
      }),
    );
  }

  private fetchRebateData(): Observable<any> {
    const year = localStorage.getItem('selectedYear') || '';
    return this.rebateContractService.getYearList({ year }).pipe(
      tap((result) => {
        const { rtnCode, rtnData } = result;
        this.permRebate = rtnCode === 0 || rtnCode === 10000;
        if ((rtnCode === 0 || rtnCode === 10000) && rtnData) {
          const dto = rtnData.rebateAmountTotalDto || {};
          this.rebateData = {
            targetRebateAmount: dto.targetRebateAmount || 0,
            settledRebateAmount: dto.settledRebateAmount || 0,
            paidRebateAmount: dto.paidRebateAmount || 0,
            latestUpdateTime: rtnData.latestUpdateTime || '',
          };
        } else {
          // 无权限或返回空数据：清零，避免残留上一个客户的返利金额
          this.rebateData = {
            targetRebateAmount: 0,
            settledRebateAmount: 0,
            paidRebateAmount: 0,
            latestUpdateTime: '',
          };
        }
      }),
      catchError(() => {
        // 接口异常：清零，避免残留上一个客户的返利金额
        this.rebateData = {
          targetRebateAmount: 0,
          settledRebateAmount: 0,
          paidRebateAmount: 0,
          latestUpdateTime: '',
        };
        return of(null);
      }),
    );
  }

  private fetchProductLines(): Observable<any> {
    const year = localStorage.getItem('selectedYear') || '';
    return this.dealerPortalService.getAnnualCbfTargetAndPurchase({ year }).pipe(
      tap((result) => {
        const { rtnCode, rtnData } = result;
        this.permProductLine = rtnCode === 0 || rtnCode === 10000;
        if ((rtnCode === 0 || rtnCode === 10000) && rtnData && rtnData.length > 0) {
          const sorted = [...rtnData].sort((a: any, b: any) => {
            const rateA = a.completionRate != null && a.completionRate !== '-' ? a.completionRate : 0;
            const rateB = b.completionRate != null && b.completionRate !== '-' ? b.completionRate : 0;
            return rateA - rateB;
          });
          this.productLines = sorted
            .slice(0, 6)
            .map((item: any, index: number) => ({
              name: item.productLineNameCn || '',
              value: item.currentMonthCompletionProgress != null && item.currentMonthCompletionProgress !== '-'
                ? Math.round(item.currentMonthCompletionProgress)
                : 0,
              color: this.productLineColorPalette[index % this.productLineColorPalette.length],
            }));
          // 采购金额可能为带千分位的字符串，先清洗为数值再按升序排序
          const toAmount = (item: any): number =>
            Number(String(item.purchaseAmount ?? '').replace(/,/g, '')) || 0;
          this.productLineAmounts = [...rtnData]
            .sort((a: any, b: any) => toAmount(a) - toAmount(b))
            .map((item: any) => ({
              name: item.productLineNameCn || '',
              amount: item.purchaseAmount != null ? String(item.purchaseAmount) : '0',
            }));
        } else {
          // 无权限或返回空数据：清空，避免残留上一个客户的产品线数据
          this.productLines = [];
          this.productLineAmounts = [];
        }
      }),
      catchError(() => {
        // 接口异常：清空，避免残留上一个客户的产品线数据
        this.productLines = [];
        this.productLineAmounts = [];
        return of(null);
      }),
    );
  }

  private fetchAnnualData(): Observable<any> {
    const year = localStorage.getItem('selectedYear') || '';
    return this.dealerPortalService.getMainContractAnnualTargetAchieve({ year }).pipe(
      tap((result) => {
        const { rtnCode, rtnData } = result;
        this.permAnnual = rtnCode === 0 || rtnCode === 10000;
        if ((rtnCode === 0 || rtnCode === 10000) && rtnData) {
          this.annualOverviewLatestUpdateTime = rtnData.latestUpdateTime || '';
          const overviews = rtnData.annualOverviews || [];
          if (overviews.length > 0) {
            const found = overviews.find(
              (item: any) => item.customerType === 'OVERVIEW_CURRENT_YEAR_PARENT',
            );
            this.annualOverview = found || overviews[0];
          } else {
            // 有权限但无年度数据：重置，避免残留上一个客户的采购目标
            this.annualOverviewLatestUpdateTime = '';
            this.annualOverview = {
              currentTargetAchievement: 0,
              annualPurchaseTarget: '0',
              actualPurchaseAmount: '0',
              quarterlyTarget: '0',
              pendingPurchaseAmountYTM: '0',
              quarterlyPendingPurchaseAmount: '0',
            };
          }
        } else {
          // 无权限或异常：重置，避免残留上一次客户的采购目标影响布局切换
          this.annualOverviewLatestUpdateTime = '';
          this.annualOverview = {
            currentTargetAchievement: 0,
            annualPurchaseTarget: '0',
            actualPurchaseAmount: '0',
            quarterlyTarget: '0',
            pendingPurchaseAmountYTM: '0',
            quarterlyPendingPurchaseAmount: '0',
          };
        }
      }),
      catchError(() => {
        // 接口异常：重置，避免残留上一次客户的采购目标影响布局切换
        this.annualOverviewLatestUpdateTime = '';
        this.annualOverview = {
          currentTargetAchievement: 0,
          annualPurchaseTarget: '0',
          actualPurchaseAmount: '0',
          quarterlyTarget: '0',
          pendingPurchaseAmountYTM: '0',
          quarterlyPendingPurchaseAmount: '0',
        };
        return of(null);
      }),
    );
  }

  private fetchNews(): void {
    this.newsLoading = true;
    this.warrantyService.apiNewsPage({
      current: 1,
      size: -1,
      channelList: ['HomePage'],
      isAdmin: false,
    }).pipe(
      tap((res) => {
        this.permNews = res.rtnCode === 0 || res.rtnCode === 10000;
        if ((res.rtnCode === 0 || res.rtnCode === 10000) && res.rtnData) {
          const records = res.rtnData.records || [];
          this.newsItems = records.map((item: any) => ({
            date: item.createTime ? moment(item.createTime).format('YYYY年MM月DD日') : '',
            title: item.title || '',
            desc: item.content || '',
            introductionFile: item.introductionFile || '',
            fileName: item.fileName || '',
          }));
        } else {
          // 无权限或返回空数据：清空，避免残留上一次的资讯列表
          this.newsItems = [];
        }
      }),
      catchError(() => {
        // 接口异常：清空，避免残留上一次的资讯列表
        this.newsItems = [];
        return of(null);
      }),
      finalize(() => { this.newsLoading = false; }),
    ).subscribe();
  }

  /**
   * 下载资讯附件：调用 /userCenterAdmin/file/download/{id}（id 取自 introductionFile 字段）。
   * 实现参考 documents.component.ts 的 onDownloadFile —— 以 blob 在当前页触发下载，不跳转、不开新标签。
   * introductionFile 无值时模板不展示入口，此处再做一次防御性判空。
   */
  downloadIntroductionFile(fileId?: string, fileName?: string): void {
    if (!fileId) { return; }
    this.documentService.downloadFile(fileId).subscribe((result: any) => {
      if (result) {
        if (result.rtnCode && result.rtnCode !== 10000) {
          this.message.error(result.rtnMsg);
          return;
        }
        const url = window.URL.createObjectURL(
          new Blob([result], { type: result.type })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName || `attachment-${fileId}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    });
  }

  private loadBannerData(): void {
    this.bannerLoading = true;
    this.loginService.getBannerTableList({
      size: 20,
      current: 1,
      title: '',
      status: 1,
      isAdmin: false,
      salesOrgVOList: [],
    }).pipe(
      tap((result) => {
        const { rtnCode, rtnData } = result;
        if ((rtnCode === 0 || rtnCode === 10000) && rtnData) {
          const records = rtnData.records || [];
          const baseUrl = `${environment.userCenterAdminUrl}/file/download/`;
          // 默认 Banner 始终置顶，接口数据拼接其后
          this.bannerList = [
            this.defaultBanner,
            ...records.map((banner: any) => ({
              image: banner.imageId
                ? `${baseUrl}${banner.imageId}`
                : 'assets/official-website/banner13.png',
              title: banner.title || '',
              navigationUrl: banner.linkUrl || '',
            })),
          ];
          if (this.bannerList.length > 1) {
            this.startBannerAutoPlay();
          }
        } else {
          // 无权限或返回空数据：仅保留默认 Banner，避免残留上一次的数据
          this.bannerList = [this.defaultBanner];
          this.currentBannerIndex = 0;
          this.stopBannerAutoPlay();
        }
      }),
      catchError(() => {
        // 接口异常：仅保留默认 Banner，避免残留上一次的数据
        this.bannerList = [this.defaultBanner];
        this.currentBannerIndex = 0;
        this.stopBannerAutoPlay();
        return of(null);
      }),
      finalize(() => { this.bannerLoading = false; }),
    ).subscribe();
  }
}
